import React, { useState } from 'react';

interface SentenceItem {
  target_sentence: string;
  words: string[];
  translation: string;
}

interface SentenceBuilderExerciseProps {
  sentences?: SentenceItem[];
  items?: SentenceItem[];  // Alias for sentences
  onSubmit: (answers: string[][]) => void;
}

/**
 * SentenceBuilderExercise Component
 *
 * Users drag and drop or click words to build correct sentences.
 */
const SentenceBuilderExercise: React.FC<SentenceBuilderExerciseProps> = ({ sentences, items, onSubmit }) => {
  const sentenceItems = sentences || items || [];
  const [builtSentences, setBuiltSentences] = useState<{ [key: number]: string[] }>({});

  const handleWordClick = (sentenceIndex: number, word: string) => {
    const current = builtSentences[sentenceIndex] || [];
    setBuiltSentences({
      ...builtSentences,
      [sentenceIndex]: [...current, word]
    });
  };

  const handleRemoveWord = (sentenceIndex: number, wordIndex: number) => {
    const current = builtSentences[sentenceIndex] || [];
    setBuiltSentences({
      ...builtSentences,
      [sentenceIndex]: current.filter((_, i) => i !== wordIndex)
    });
  };

  const handleClear = (sentenceIndex: number) => {
    setBuiltSentences({
      ...builtSentences,
      [sentenceIndex]: []
    });
  };

  const handleSubmit = () => {
    const answers = sentenceItems.map((_, index) => builtSentences[index] || []);
    onSubmit(answers);
  };

  const getAvailableWords = (sentenceIndex: number, allWords: string[]) => {
    const used = builtSentences[sentenceIndex] || [];
    return allWords.filter(word => !used.includes(word) || used.filter(w => w === word).length < allWords.filter(w => w === word).length);
  };

  const isComplete = sentenceItems.every((sentence, index) => {
    const built = builtSentences[index] || [];
    return built.length === sentence.words.length;
  });

  return (
    <div className="space-y-8">
      {sentenceItems.map((sentence, sIndex) => {
        const builtSentence = builtSentences[sIndex] || [];
        const availableWords = getAvailableWords(sIndex, sentence.words);

        return (
          <div key={sIndex} className="card bg-base-200">
            <div className="card-body">
              <div className="flex items-center gap-2 mb-4">
                <span className="badge badge-primary">{sIndex + 1}</span>
                <span className="text-sm text-base-content/70">{sentence.translation}</span>
              </div>

              {/* Built Sentence Area */}
              <div className="min-h-[4rem] p-4 rounded-lg border-2 border-dashed border-primary/30 bg-base-100">
                {builtSentence.length === 0 ? (
                  <p className="text-center text-base-content/50">Click words below to build the sentence</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {builtSentence.map((word, wIndex) => (
                      <button
                        key={wIndex}
                        onClick={() => handleRemoveWord(sIndex, wIndex)}
                        className="btn btn-primary btn-sm gap-2"
                      >
                        {word}
                        <span className="text-xs opacity-70">✕</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear Button */}
              {builtSentence.length > 0 && (
                <div className="flex justify-end">
                  <button
                    onClick={() => handleClear(sIndex)}
                    className="btn btn-ghost btn-xs"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Available Words */}
              <div className="divider">Available Words</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {availableWords.map((word, wIndex) => (
                  <button
                    key={wIndex}
                    onClick={() => handleWordClick(sIndex, word)}
                    className="btn btn-outline btn-sm"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete
            ? 'Submit Sentences'
            : `Build all sentences (${Object.keys(builtSentences).filter(k => builtSentences[parseInt(k)]?.length === sentenceItems[parseInt(k)]?.words.length).length}/${sentenceItems.length})`
          }
        </button>
      </div>
    </div>
  );
};

export default SentenceBuilderExercise;
